import { Edge, MarkerType, Node } from 'reactflow';
import { v4 as uuidv4 } from 'uuid';
import { NodeData } from './types';
import { createEmptyNode } from './utils';

const PATH_SEPARATOR = '\x1f';

export interface PrefixLengthParseResult {
  lengths: PrefixLengthStep[];
  error: string | null;
}

export interface PrefixLengthStep {
  length: number;
  repeat: boolean;
}

export interface PrefixMatch {
  node: Node<NodeData>;
  chunks: string[];
  prefixes: string[];
  generatedPath: string[];
  pathKey: string | null;
}

export interface PrefixSkippedNode {
  node: Node<NodeData>;
  reason: string;
}

export interface PrefixRestructurePlan {
  matches: PrefixMatch[];
  skipped: PrefixSkippedNode[];
  generatedPathCount: number;
  totalPathCount: number;
}

export interface PrefixRestructureResult {
  nodes: Node<NodeData>[];
  edges: Edge[];
  affectedCount: number;
  generatedCount: number;
}

export const parsePrefixLengthsInput = (value: string): PrefixLengthParseResult => {
  const tokens = value.split(/[\s,;]+/).filter(Boolean);
  if (tokens.length === 0) {
    return { lengths: [], error: null };
  }

  const lengths: PrefixLengthStep[] = [];
  for (const token of tokens) {
    if (!/^\d+\*?$/.test(token)) {
      return { lengths: [], error: 'Use positive whole numbers, optionally followed by *, separated by commas or spaces.' };
    }

    const repeat = token.endsWith('*');
    const length = Number(repeat ? token.slice(0, -1) : token);
    if (!Number.isSafeInteger(length) || length <= 0) {
      return { lengths: [], error: 'Lengths must be positive whole numbers.' };
    }
    lengths.push({ length, repeat });
  }

  return { lengths, error: null };
};

export const splitNameByPrefixLengths = (name: string, lengths: PrefixLengthStep[]): string[] | null => {
  const value = name.trim();
  if (!value || lengths.length === 0) {
    return null;
  }

  const chunks: string[] = [];
  let offset = 0;

  for (const step of lengths) {
    if (offset >= value.length) break;

    do {
      const remaining = value.length - offset;
      const nextLength = Math.min(step.length, remaining);
      chunks.push(value.slice(offset, offset + nextLength));
      offset += nextLength;
    } while (step.repeat && offset < value.length);
  }

  return chunks.length > 0 ? chunks : null;
};

const createPrefixMatch = (node: Node<NodeData>, chunks: string[]): PrefixMatch => {
  const name = (node.data.name || '').trim();
  let cumulative = '';
  const prefixes = chunks.map(chunk => {
    cumulative += chunk;
    return cumulative;
  });
  const generatedPath = prefixes[prefixes.length - 1] === name
    ? prefixes.slice(0, -1)
    : prefixes;

  return {
    node,
    chunks,
    prefixes,
    generatedPath,
    pathKey: generatedPath[generatedPath.length - 1] || null
  };
};

const collectPathKeys = (matches: PrefixMatch[]): string[] => {
  const pathKeys = new Set<string>();

  matches.forEach(match => {
    match.generatedPath.forEach(pathKey => pathKeys.add(pathKey));
  });

  return Array.from(pathKeys).sort((a, b) => {
    const depthDelta = a.length - b.length;
    return depthDelta || a.localeCompare(b);
  });
};

const createPathParentMap = (matches: PrefixMatch[]): Map<string, string | null> => {
  const parentByPath = new Map<string, string | null>();

  matches.forEach(match => {
    match.generatedPath.forEach((pathKey, index) => {
      if (!parentByPath.has(pathKey)) {
        parentByPath.set(pathKey, index === 0 ? null : match.generatedPath[index - 1]);
      }
    });
  });

  return parentByPath;
};

const createReusablePathNodeMap = (
  matches: PrefixMatch[],
  additionalNodes: Node<NodeData>[] = []
): Map<string, Node<NodeData>> => {
  const nodesByName = new Map<string, Node<NodeData>[]>();

  [...matches.map(match => match.node), ...additionalNodes].forEach(node => {
    const name = (node.data.name || '').trim();
    if (!name) return;
    const list = nodesByName.get(name) || [];
    list.push(node);
    nodesByName.set(name, list);
  });

  const reusable = new Map<string, Node<NodeData>>();
  nodesByName.forEach((nodesForName, name) => {
    if (nodesForName.length === 1) {
      reusable.set(name, nodesForName[0]);
    }
  });

  return reusable;
};

export const createPrefixRestructurePlan = (
  candidates: Node<NodeData>[],
  lengths: PrefixLengthStep[],
  additionalReusableNodes: Node<NodeData>[] = []
): PrefixRestructurePlan => {
  const matches: PrefixMatch[] = [];
  const skipped: PrefixSkippedNode[] = [];

  candidates.forEach(node => {
    const chunks = splitNameByPrefixLengths(node.data.name || '', lengths);
    if (!chunks) {
      skipped.push({ node, reason: 'Name is blank.' });
      return;
    }

    matches.push(createPrefixMatch(node, chunks));
  });

  const pathKeys = collectPathKeys(matches);
  const reusablePathNodes = createReusablePathNodeMap(matches, additionalReusableNodes);
  const generatedPathCount = pathKeys.filter(pathKey => !reusablePathNodes.has(pathKey)).length;

  return {
    matches,
    skipped,
    generatedPathCount,
    totalPathCount: pathKeys.length
  };
};

export const buildPrefixRestructure = (
  nodes: Node<NodeData>[],
  edges: Edge[],
  parentNodeId: string,
  candidates: Node<NodeData>[],
  lengths: PrefixLengthStep[]
): PrefixRestructureResult | null => {
  const parentNode = nodes.find(node => node.id === parentNodeId) || null;
  const plan = createPrefixRestructurePlan(candidates, lengths, parentNode ? [parentNode] : []);
  if (plan.matches.length === 0) return null;

  const pathKeys = collectPathKeys(plan.matches);
  const parentByPath = createPathParentMap(plan.matches);
  const reusablePathNodes = createReusablePathNodeMap(plan.matches, parentNode ? [parentNode] : []);
  const pathNodeIds = new Map<string, string>();
  const generatedNodes: Node<NodeData>[] = [];

  pathKeys.forEach(pathKey => {
    const reusableNode = reusablePathNodes.get(pathKey);
    if (reusableNode) {
      pathNodeIds.set(pathKey, reusableNode.id);
      return;
    }

    const id = uuidv4();
    const node = createEmptyNode(id, null, false);
    generatedNodes.push({
      ...node,
      data: {
        ...node.data,
        name: pathKey,
        iconPath: 'Layout',
        color: '#0d9488'
      }
    });
    pathNodeIds.set(pathKey, id);
  });

  const nextNodes = nodes.concat(generatedNodes);

  const targetPathNodeIds = new Set(pathNodeIds.values());
  plan.matches.forEach(match => targetPathNodeIds.add(match.node.id));
  targetPathNodeIds.delete(parentNodeId);

  const nextEdges = edges.filter(edge => !targetPathNodeIds.has(edge.target));
  const existingConnections = new Set(nextEdges.map(edge => `${edge.source}${PATH_SEPARATOR}${edge.target}`));
  const addedEdges: Edge[] = [];

  pathKeys.forEach(pathKey => {
    const targetId = pathNodeIds.get(pathKey);
    if (!targetId) return;

    const parentPathKey = parentByPath.get(pathKey) || null;
    const sourceId = parentPathKey ? pathNodeIds.get(parentPathKey) : parentNodeId;
    if (!sourceId || sourceId === targetId) return;

    const connectionKey = `${sourceId}${PATH_SEPARATOR}${targetId}`;
    if (existingConnections.has(connectionKey)) return;

    existingConnections.add(connectionKey);
    addedEdges.push({
      id: `e-${sourceId}-${targetId}`,
      source: sourceId,
      target: targetId,
      type: 'base',
      markerEnd: { type: MarkerType.ArrowClosed }
    });
  });

  plan.matches.forEach(match => {
    const sourceId = match.pathKey ? pathNodeIds.get(match.pathKey) : parentNodeId;
    if (!sourceId || sourceId === match.node.id) return;

    const connectionKey = `${sourceId}${PATH_SEPARATOR}${match.node.id}`;
    if (existingConnections.has(connectionKey)) return;

    existingConnections.add(connectionKey);
    addedEdges.push({
      id: `e-${sourceId}-${match.node.id}`,
      source: sourceId,
      target: match.node.id,
      type: 'base',
      markerEnd: { type: MarkerType.ArrowClosed }
    });
  });

  return {
    nodes: nextNodes,
    edges: [...nextEdges, ...addedEdges],
    affectedCount: plan.matches.length,
    generatedCount: generatedNodes.length
  };
};
